import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const PLAN = {
  monthly: {
    price: "54.99",
    unit: "US$",
    suffix: "/mes",
    label: "Mensual",
    note: "Sin permanencia. Cambia a anual cuando quieras. Hasta 200 comprobantes mensuales sin coste añadido (cada extra son tan solo 10 pesos).",
  },
  yearly: {
    price: "45",
    unit: "US$",
    suffix: "/mes",
    label: "Anual",
    savings: "ahorra 18%",
    note: "Ahorras US$120 al año.",
  },
};

const BASE_INCLUDED = [
  { label: "Cobro recurrente automatizado", included: true },
  { label: "Reintentos inteligentes de pagos fallidos", included: true },
  { label: "Adquirentes locales (CardNet, Azul, Portal, Mio)", included: true },
  { label: "Checkout con tu marca (logo, colores, video)", included: true },
  { label: "Múltiples usuarios con permisos por rol", included: true },
  { label: "Split payment (hasta 4 pagadores) y cuotas", included: true },
  { label: "Dashboard con métricas y proyecciones", included: true },
  { label: "Onboarding humano y soporte prioritario por WhatsApp", included: true },
];

export default function Pricing({ onDemoClick }) {
  const [cycle, setCycle] = useState("yearly");
  const isYearly = cycle === "yearly";
  const current = isYearly ? PLAN.yearly : PLAN.monthly;

  const INCLUDED = [
    ...BASE_INCLUDED,
    {
      label: isYearly
        ? "Facturación electrónica ilimitada durante la membresía"
        : "200 facturas con facturación electrónica gratis todos los meses",
      included: true,
      highlight: isYearly,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-8 md:py-12 bg-[#F2EDE3]"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-base font-bold uppercase tracking-[0.2em] text-[#111111]">
            Precio simple
          </span>
          <h2 className="mt-4 font-['Cabinet_Grotesk'] font-black tracking-tighter leading-[0.95] text-[#075a22] text-4xl sm:text-5xl md:text-6xl">
            Un solo plan. Todo incluido.
          </h2>
          <p className="mt-5 text-xl text-[#111111]/85">
            Empieza cuando quieras. Cancela cuando quieras. Facturación
            electrónica ilimitada mientras seas miembro.
          </p>
        </motion.div>

        {/* Toggle Monthly / Yearly */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          className="flex justify-center mb-10"
          data-testid="pricing-cycle-toggle"
        >
          <div className="inline-flex items-center bg-white border border-[#111111]/10 rounded-full p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setCycle("monthly")}
              data-testid="cycle-monthly"
              className="relative px-7 py-3 text-base font-bold rounded-full transition-colors"
            >
              {!isYearly && (
                <motion.span
                  layoutId="cycle-thumb"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  className="absolute inset-0 rounded-full bg-[#111111]"
                  aria-hidden="true"
                />
              )}
              <span
                className={`relative z-10 ${
                  !isYearly ? "text-white" : "text-[#111111]/70 hover:text-[#111111]"
                }`}
              >
                Mensual
              </span>
            </button>
            <button
              type="button"
              onClick={() => setCycle("yearly")}
              data-testid="cycle-yearly"
              className="relative px-6 py-2.5 text-sm font-bold rounded-full transition-colors flex items-center gap-2"
            >
              {isYearly && (
                <motion.span
                  layoutId="cycle-thumb"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  className="absolute inset-0 rounded-full bg-[#111111]"
                  aria-hidden="true"
                />
              )}
              <span
                className={`relative z-10 ${
                  isYearly ? "text-white" : "text-[#111111]/70 hover:text-[#111111]"
                }`}
              >
                Anual
              </span>
              <span
                className={`relative z-10 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                  isYearly
                    ? "bg-[#a68a6d] text-white"
                    : "bg-[#075a22]/10 text-[#075a22]"
                }`}
              >
                ahorra 18%
              </span>
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          transition={{ duration: 0.6 }}
          data-testid="pricing-plan-card"
          className="relative bg-white border border-[#111111]/10 rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(17,17,17,0.2)]"
        >
          <div className="grid md:grid-cols-2">
            {/* Left: price */}
            <div className="relative p-8 md:p-12 bg-[#111111] text-white">
              <div
                className="absolute -top-40 -right-40 w-[400px] h-[400px] rounded-full bg-[#075a22]/25 blur-3xl pointer-events-none"
                aria-hidden="true"
              />

              <div className="relative">

                <AnimatePresence mode="wait">
                  <motion.div
                    key={cycle}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-white/70">
                        {current.unit}
                      </span>
                      <span
                        className="font-['Cabinet_Grotesk'] font-black leading-none text-white text-6xl md:text-7xl"
                        data-testid="pricing-amount"
                      >
                        {current.price}
                      </span>
                      <span className="text-base font-medium text-white/70 ml-1">
                        {current.suffix}
                      </span>
                    </div>

                    {isYearly && (
                      <p className="mt-2 text-base text-white/80">
                        {PLAN.yearly.note}
                      </p>
                    )}
                    {!isYearly && (
                      <p className="mt-2 text-base text-white/80">
                        {PLAN.monthly.note}
                      </p>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="my-8 h-px bg-white/15" />

                <p className="text-base text-white/85 leading-relaxed">
                  Además, solo cobramos <strong className="text-white">2%</strong>{" "}
                  sobre el volumen procesado por Kubo Payments.{" "}
                  <span className="text-white/70">
                    La comisión del 2% aplica solo a pagos online procesados por Kubo.
                  </span>
                  La comisión del adquirente (CardNet, Azul, Portal o Mio) es aparte
                  y depende de tu contrato con ellos.
                </p>

                <div className="mt-8 flex flex-col gap-3 w-full">
                  <button
                    onClick={onDemoClick}
                    data-testid="pricing-cta"
                    className="inline-flex items-center justify-center gap-2 bg-[#a68a6d] hover:bg-[#8f7659] text-white font-bold px-6 py-3.5 rounded-full transition-all hover:scale-[1.03] shadow-[0_16px_40px_rgba(166,138,109,0.35)] w-full text-sm"
                  >
                    Agendar demo
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                  <a
                    href="http://app.kubopayments.com/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pricing-cta-register"
                    className="inline-flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#111111] font-bold px-6 py-3.5 rounded-full transition-all hover:scale-[1.03] border border-[#8f7659] w-full text-sm"
                  >
                    Registro gratuito
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                </div>
              </div>
            </div>

            {/* Right: features */}
            <div className="p-8 md:p-12 bg-white">
              <p className="text-base font-bold uppercase tracking-[0.18em] text-[#111111] mb-6">
                Todo incluido
              </p>
              <ul className="space-y-3.5">
                {INCLUDED.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        item.highlight ? "bg-[#075a22]" : "bg-[#a68a6d]/15"
                      }`}
                    >
                      <Check
                        className={`w-3 h-3 ${
                          item.highlight ? "text-white" : "text-[#a68a6d]"
                        }`}
                        strokeWidth={3}
                      />
                    </div>
                    <span
                      className={`text-base leading-relaxed ${
                        item.highlight
                          ? "font-bold text-[#111111]"
                          : "text-[#111111]"
                      }`}
                    >
                      {item.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        <div className="mt-10 p-6 bg-white border border-[#111111]/10 rounded-2xl">
          <p className="text-base font-bold text-[#111111] mb-3">
            Aclaración importante sobre la comisión
          </p>
          <p className="text-base text-[#111111]/85 leading-relaxed">
            <strong className="text-[#111111]">
              La comisión del 2% aplica solo a pagos online procesados por Kubo.
            </strong>{" "}
            No aplica a cobros manuales (efectivo, transferencia, POS presencial)
          </p>
        </div>

        <p className="mt-8 text-center text-base text-[#111111]/80 max-w-2xl mx-auto">
          Sin costo de implementación. Sin planes complicados. Soporte directo.
        </p>
      </div>
    </section>
  );
}
